export type ErrorKind =
  | "validation"
  | "not_found"
  | "conflict"
  | "rate_limited"
  | "unavailable"
  | "timeout"
  | "unknown";

export interface RevisionRef {
  no: number;
  status: string;
}

export interface MappedError {
  kind: ErrorKind;
  message: string;
  alternatives?: string[];
  retryAfter?: number;
  availableRevisions?: RevisionRef[];
}

export function mapApiError(
  status: number,
  code: string,
  body: Record<string, unknown>,
): MappedError {
  if (status === 400 && code === "validation_error") {
    const detail = body.detail as Array<{ loc?: string[]; msg?: string }> | undefined;
    const first = detail?.[0];
    const field = first?.loc?.slice(1).join(".") ?? "input";
    return { kind: "validation", message: `${field}: ${first?.msg ?? "invalid"}` };
  }

  if (status === 404) {
    const errObj =
      typeof body.error === "object" && body.error !== null
        ? (body.error as {
            details?: { available_revisions?: Array<{ no: number; status: string }> };
            message?: string;
          })
        : null;
    const availRevs = errObj?.details?.available_revisions;
    if (code === "NotFoundError" && availRevs && availRevs.length > 0) {
      return {
        kind: "not_found",
        message: "Revision not found",
        availableRevisions: availRevs,
      };
    }
    const detail = typeof body.detail === "string" ? body.detail : "Not found";
    return { kind: "not_found", message: detail };
  }

  if (status === 409) {
    if (code === "etag_mismatch") {
      return { kind: "conflict", message: "Changed elsewhere — reload to get the latest" };
    }
    if (code === "dataset_not_ready") {
      const details = (body.error as { details?: { current_status?: string } })?.details;
      const currentStatus = details?.current_status ?? "not ready";
      return {
        kind: "conflict",
        message: `Dataset is currently "${currentStatus}" — try again when it's ready`,
      };
    }
    if (code === "capability_mismatch") {
      const details = (body.error as { details?: { alternatives?: string[] } })?.details;
      return {
        kind: "conflict",
        message: "Not available on current hardware",
        alternatives: details?.alternatives,
      };
    }
    return { kind: "conflict", message: "Conflict" };
  }

  if (status === 429) {
    return { kind: "rate_limited", message: "Rate limited — please retry shortly" };
  }

  if (status === 502 || status === 503) {
    if (code === "pool_unavailable") {
      return { kind: "unavailable", message: "Inference service busy — try again shortly" };
    }
    return { kind: "unavailable", message: "Service unavailable — please retry" };
  }

  if (status === 504) {
    return { kind: "timeout", message: "Request timed out — it may still be running" };
  }

  return { kind: "unknown", message: "An unexpected error occurred" };
}

export function getErrorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error && typeof error === "object" && "detail" in error) {
    // biome-ignore lint/suspicious/noExplicitAny: API error shape varies
    const detail = (error as any).detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return JSON.stringify(detail);
  }
  return "Unknown error";
}
